/**
 * Adaptive garrisons (IDEAS X17; MGSV's outposts): a garrison that was attacked remembers how, and changes what it
 * brings and how it opens next time — keyed to the chronicle of the last battle, deterministic, behind the sector
 * rule `adaptiveGarrisons`. What it remembers: where the attack came from (the attacker's lane), sneak or assault,
 * loud or quiet, and what it cost. What it does about it:
 *   - `watch`  (after a sneak): the garrison is never unaware again here — the next opening starts at least suspicious,
 *               and the stand-in builds a sensor first;
 *   - `face`   (after an attack from a lane): the garrison deploys facing that lane, between it and the system;
 *   - `refit`  (after a sneak, or losing half or more): its squads refit — sentinel against sneaks, entrenched against
 *               a loud assault — when the faction can pay (56.11's refit; it is not free).
 * Scouting reports show the change (the known system carries the summary). Pure.
 */
import type { BattleOutcome, BattleSetup, FactionId, GarrisonMemory, SectorState, SectorSystem, SystemId } from './types';

export type GarrisonAdaptation = GarrisonMemory['adaptations'][number];

/** What the garrison takes from the battle just fought here, on top of what it remembered. */
export function rememberAttack(prev: GarrisonMemory | undefined, setup: BattleSetup, outcome: BattleOutcome, day: number): GarrisonMemory | undefined {
  const defender = setup.opening.defender;
  if (!defender) return prev;
  const side = setup.sides.find(s => s.faction === defender);
  const attacker = setup.sides.find(s => s.faction !== defender);
  if (!side || !attacker) return prev;
  const committed = side.units.length;
  const lost = side.units.filter(u => { const r = outcome.units.find(x => x.unitId === u.unitId); return r && !r.alive; }).length;
  const lostShare = committed ? lost / committed : 0;
  const sneak = setup.opening.attackerStance === 'sneak';
  const adaptations: GarrisonAdaptation[] = [];
  if (sneak) adaptations.push('watch');
  if (attacker.arrival) adaptations.push('face');
  if (sneak || lostShare >= 0.5) adaptations.push('refit');
  return {
    attacks: (prev?.attacks ?? 0) + 1,
    ...(attacker.arrival ? { lastFrom: attacker.arrival.from } : {}),
    lastStance: setup.opening.attackerStance,
    lastLoud: outcome.wentLoud,
    lostShare,
    adaptations,
    since: day,
  };
}

/** The kit a refitting garrison takes: sentinel against sneaks (sees far), entrenched against a loud assault (takes hits). */
export function refitKitFor(memory: GarrisonMemory): 'sentinel' | 'entrenched' {
  return memory.lastStance === 'sneak' ? 'sentinel' : 'entrenched';
}

/** The memory in words, for the system panel and a scout's report. */
export function memorySummary(memory: GarrisonMemory, state: Pick<SectorState, 'systems'>): string {
  const from = memory.lastFrom ? state.systems.find(s => s.id === memory.lastFrom)?.name ?? memory.lastFrom : null;
  const parts: string[] = [];
  parts.push(`attacked ${memory.attacks} time${memory.attacks === 1 ? '' : 's'}, last by ${memory.lastStance === 'sneak' ? 'a sneak' : 'assault'}${from ? ` from ${from}` : ''}`);
  const does: string[] = [];
  if (memory.adaptations.includes('watch')) does.push('watching for sneaks');
  if (memory.adaptations.includes('face') && from) does.push(`facing ${from}`);
  if (memory.adaptations.includes('refit')) does.push(`refitting ${refitKitFor(memory)}`);
  if (does.length) parts.push(does.join(', '));
  return parts.join('; ');
}

/** The holder's deploy facing from its memory: toward the lane the last attack came by. */
export function facingFor(memory: GarrisonMemory | undefined, sys: SectorSystem, systems: readonly SectorSystem[]): { dx: number; dy: number } | undefined {
  if (!memory?.adaptations.includes('face') || !memory.lastFrom) return undefined;
  const origin = systems.find(s => s.id === memory.lastFrom);
  if (!origin) return undefined;
  return { dx: origin.x - sys.x, dy: origin.y - sys.y };
}

/** Whether the garrison here is watching (the opening starts at least suspicious). */
export function garrisonWatches(sys: Pick<SectorSystem, 'garrisonMemory'>): boolean {
  return !!sys.garrisonMemory?.adaptations.includes('watch');
}

export const isFaction = (x: unknown): x is FactionId => typeof x === 'string';
export const sameSystem = (a: SystemId, b: SystemId): boolean => a === b;
